import '@ev-tech/user-account-modules/index.css';
import '@pkgs/components/search-module-tokens.css';
import 'core-js/actual/array/to-reversed';
import 'core-js/actual/array/to-sorted';
import 'core-js/actual/object/group-by';
import 'leaflet/dist/leaflet.css';
import './index.css';
import { Trans } from 'react-i18next';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import NextLink from 'next/link';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import type { AppProps } from 'next/app';
import { appWithTranslation, useTranslation } from 'next-i18next/pages';
import { SpeedInsights } from '@vercel/speed-insights/next';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { type DehydratedState, HydrationBoundary, QueryClientProvider } from '@tanstack/react-query';
import { isBrowserStorageAvailable } from '@pkgs/components/utils/storageUtils';
import { extractParamsFromUrl } from '@pkgs/components/utils/searchParams';
import { type PageTrackingTypes } from '@pkgs/components/types/tracking';
import { BreakpointProvider } from '@pkgs/components/providers/BreakpointProvider';
import { createQueryClient } from '@pkgs/components/hooks/queries/queryClient';
import { type TFunction, type TransComponent, UserAccountProvider as UserAccountAuthProvider } from '@pkgs/components';
import { DEFAULT_OPTIONS } from '@pkgs/api/utils/filters';
import { MarketCountryCode } from '@pkgs/api/index';
import { GoogleTagManager } from '@next/third-parties/google';
import { UserAccountProvider } from '@ev-tech/user-account-modules';
import { StatusMessageProvider } from '@ev-tech/ui-core-components/StatusMessageProvider';
import { RoutingProvider } from '@ev-tech/ui-core-components/RoutingProvider';
import { useModalState } from '@ev-tech/ui-core-components/Modal';
import { type StoryblokDatasourceEntry, type StoryblokNavigation } from '@ev-tech/ui-core-components/cms';
import { DatadogPagesRouter, ErrorBoundary } from '@datadog/browser-rum-nextjs';

import { getLicencePartnerGAAccount } from '@/utils/tracking';
import { joinUs } from '@/utils/join-us';
import { isMobileDevice } from '@/utils/isMobileDevice';
import { setLanguageCookie } from '@/utils/cookieUtils';
import { createAppMetadata } from '@/utils/appMetadata';
import { SettingsProvider } from '@/providers/SettingsProvider';
import { DidomiProvider } from '@/providers/DidomiContextProvider';
import { AppChatbotProvider } from '@/providers/AppChatbotProvider';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { usePageTrackingTypes } from '@/hooks/usePageTrackingTypes';
import { type FeatureFlag } from '@/consts/featureFlags';
import { ErrorPage } from '@/components/common/ErrorPage/ErrorPage';

import nextI18NextConfig from '../../next-i18next.config';

const BlockedLocalStorageDialog = dynamic(() => import('@/components/common/BlockedLocalStorage/BlockedLocalStorageDialog'), {
  ssr: false,
});

const ReactQueryDevtoolsProduction = dynamic(
  () => import('@tanstack/react-query-devtools/build/modern/production.js').then((mod) => mod.ReactQueryDevtools),
  { ssr: false },
);

const UserAccountTransComponent: TransComponent = ({ components, i18nKey, t, values }) => {
  return <Trans components={components as never} i18nKey={i18nKey as never} t={t as never} values={values} />;
};

export type AppPageProps = {
  pageLanguages: string[] | null;
  pageTrackingTypes?: PageTrackingTypes;
  spearHeadNavigation?: StoryblokNavigation | null;
  externalLinkDomains?: StoryblokDatasourceEntry[] | null;
  countryLinks?: StoryblokDatasourceEntry[] | null;
  featureFlags?: Record<FeatureFlag, boolean>;
  dehydratedState?: DehydratedState;
};

const initMobileDevTools = async () => {
  const { default: eruda } = await import('eruda');
  eruda.init();
};

const SearchApp = (props: AppProps<AppPageProps>) => {
  const { Component, pageProps } = props;
  const [hasLicencePartnerGAAccount, setHasLicencePartnerGAAccount] = useState(false);
  const [hasQueryDevtools, setHasQueryDevtools] = useState(false);
  const { t } = useTranslation(TRANSLATION_NAMESPACE.user_account);
  const router = useRouter();
  const inferredPageTrackingTypes = usePageTrackingTypes();
  const { countryCode = MarketCountryCode.DE, language = DEFAULT_OPTIONS.language } = extractParamsFromUrl(router.asPath);

  const queryClient = useRef(createQueryClient());

  useEffect(() => {
    joinUs();

    if (process.env.NODE_ENV === 'development' && isMobileDevice(navigator.userAgent)) {
      void initMobileDevTools();
    }

    // Debugging utilities
    window.toggleDevtools = () => setHasQueryDevtools((value) => !value);
    window.queryClient = queryClient.current;

    const hasProductionDevtoolsCookie = pageProps.featureFlags?.queryDevtools;

    if (hasProductionDevtoolsCookie && process.env.NODE_ENV === 'production') {
      setHasQueryDevtools(true);
    }

    const licencePartnerGaAccount = getLicencePartnerGAAccount();
    if (licencePartnerGaAccount) {
      setHasLicencePartnerGAAccount(true);
      window.dataLayer?.push({
        event: 'licencePartnerGAAccount',
        licencePartnerGA: licencePartnerGaAccount,
      });
    }
    setLanguageCookie(language);
  }, []);

  useEffect(() => {
    window.evAppMetadata = createAppMetadata({
      featureFlags: pageProps.featureFlags,
    });
  }, [pageProps.featureFlags]);

  const blockedLocalStorageDialogState = useModalState();
  const { open: openBlockedLocalStorageDialog } = blockedLocalStorageDialogState;

  useEffect(() => {
    if (!isBrowserStorageAvailable('localStorage')) {
      openBlockedLocalStorageDialog();
    }
  }, [openBlockedLocalStorageDialog]);

  return (
    <>
      <Head>
        <title>Search</title>
        <meta name="viewport" content="minimum-scale=1, initial-scale=1, width=device-width, interactive-widget=resizes-visual" />
        {/* it resolves a hydration issue on ios https://github.com/vercel/next.js/issues/38290 */}
        <meta name="format-detection" content="telephone=no" />
        <meta name="theme-color" content="#ffffff" />

        <link rel="preconnect" href="https://uploadcare.engelvoelkers.com" />

        <link rel="preconnect" href="https://fonts.engelvoelkers.com" />

        <link rel="apple-touch-icon" sizes="180x180" href="https://uploadcare.engelvoelkers.com/20367ee3-b0ec-4b47-a7a4-a9f6fe0f23e9/" />
        <link rel="icon" type="image/png" sizes="32x32" href="https://uploadcare.engelvoelkers.com/11642d33-556d-40ea-91fd-bb8e9a93f200/" />
        <link rel="icon" type="image/png" sizes="16x16" href="https://uploadcare.engelvoelkers.com/4af56219-669f-4b35-9760-d11efef10cb7/" />
        <link rel="icon" href="https://uploadcare.engelvoelkers.com/9b1114be-3ea4-4421-8130-4d6ce3035728/" />
        <link rel="shortcut icon" href="https://uploadcare.engelvoelkers.com/9b1114be-3ea4-4421-8130-4d6ce3035728/" />
        <link
          rel="preload"
          href="https://fonts.engelvoelkers.com/EngelVoelkersHead_W_Wght.woff2"
          as="font"
          type="font/woff2"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="https://fonts.engelvoelkers.com/EngelVoelkersText_W_Wght.woff2"
          as="font"
          type="font/woff2"
          crossOrigin=""
        />
        <link
          rel="search"
          type="application/opensearchdescription+xml"
          title="Engel & Völkers"
          href={`https://www.engelvoelkers.com/${language}/opensearch.xml`}
        />
      </Head>

      {process.env.NODE_ENV === 'production' && process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID && (
        <>
          <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID} />
          <SpeedInsights
            sampleRate={0.1}
            scriptSrc="https://new-search.engelvoelkers.com/_vercel/speed-insights/script.js"
            endpoint="https://new-search.engelvoelkers.com/_vercel/speed-insights/vitals"
          />
        </>
      )}
      <DatadogPagesRouter />
      <RoutingProvider linkComponent={NextLink} router={router}>
        <ErrorBoundary fallback={ErrorPage}>
          <DidomiProvider>
            <QueryClientProvider client={queryClient.current}>
              <HydrationBoundary state={pageProps.dehydratedState}>
                <ReactQueryDevtools initialIsOpen={false} />
                {hasQueryDevtools && (
                  <Suspense fallback={null}>
                    <ReactQueryDevtoolsProduction />
                  </Suspense>
                )}
                <SettingsProvider>
                  <StatusMessageProvider>
                    <UserAccountProvider prefetchUI={false} publishableKey={process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY}>
                      <UserAccountAuthProvider
                        translationResolver={t as TFunction}
                        language={language}
                        countryCode={countryCode}
                        transComponent={UserAccountTransComponent}
                        tracking={pageProps.pageTrackingTypes ?? inferredPageTrackingTypes}
                      >
                        <BreakpointProvider>
                          {blockedLocalStorageDialogState.isOpen && (
                            <BlockedLocalStorageDialog modalState={blockedLocalStorageDialogState} />
                          )}
                          <AppChatbotProvider
                            countryCode={countryCode}
                            language={language}
                            enabled={pageProps.featureFlags?.isChatbotEnabled ?? false}
                          >
                            <Component hasLicencePartnerGAAccount={hasLicencePartnerGAAccount} {...pageProps} />
                          </AppChatbotProvider>
                        </BreakpointProvider>
                      </UserAccountAuthProvider>
                    </UserAccountProvider>
                  </StatusMessageProvider>
                </SettingsProvider>
              </HydrationBoundary>
            </QueryClientProvider>
          </DidomiProvider>
        </ErrorBoundary>
      </RoutingProvider>
    </>
  );
};

export default appWithTranslation<AppProps>(SearchApp, nextI18NextConfig) as React.ComponentType<AppProps>;
